import React from 'react';
import Page from './Page';

const PrivacyPage = () => (
  <Page
    eyebrow="LEGAL"
    title="Privacy Policy"
    subtitle="Effective date: April 2026"
  >
    <p>
      iCraftz™ ("we", "our", "us") respects your privacy and is committed to
      protecting your personal data. This policy describes what information
      we collect, how we use it, and your rights, in compliance with India's
      Information Technology Act, 2000 (and the Information Technology
      (Reasonable Security Practices and Procedures and Sensitive Personal
      Data or Information) Rules, 2011), the Digital Personal Data Protection
      Act, 2023, and where applicable, the EU General Data Protection
      Regulation (GDPR).
    </p>

    <h2>1. What We Collect</h2>
    <p>When you join our waitlist or contact us, we may collect:</p>
    <ul>
      <li><strong>Email address</strong> — required to send platform updates and launch invites.</li>
      <li><strong>Name</strong> — when you fill our partner or contact form.</li>
      <li><strong>Language preference</strong> — to serve you content in your chosen language.</li>
      <li><strong>WhatsApp number</strong> — if you optionally provide it for community access.</li>
      <li><strong>Anonymous usage data</strong> — basic analytics such as pages viewed and broad geographic region. We do not track you across third-party sites.</li>
    </ul>

    <h2>2. How We Use It</h2>
    <p>We use your data only for the following purposes:</p>
    <ul>
      <li>To send you launch announcements, product updates and platform notifications.</li>
      <li>To respond to inquiries from authors, publishers and partners.</li>
      <li>To improve our content and language coverage based on aggregated trends.</li>
      <li>To comply with applicable law.</li>
    </ul>
    <p>
      We <strong>do not</strong> sell, rent or share your personal data with
      advertisers, data brokers or any unaffiliated third party for marketing
      purposes.
    </p>

    <h2>3. Third-Party Services</h2>
    <p>To run iCraftz, we rely on the following processors:</p>
    <ul>
      <li><strong>MongoDB Atlas</strong> — encrypted database storage of waitlist and contact submissions.</li>
      <li><strong>Resend</strong> — transactional email delivery (welcome, partner replies, admin notifications).</li>
      <li><strong>Anthropic / OpenAI APIs</strong> — AI processing for translations and summary generation. Inputs are not used to train third-party models.</li>
      <li><strong>Google Drive</strong> — temporary hosting of audio podcast assets where used.</li>
    </ul>
    <p>
      Each of these vendors maintains its own privacy practices. You can review
      their policies at their respective websites.
    </p>

    <h2>4. Your Rights</h2>
    <p>You have the right to:</p>
    <ul>
      <li><strong>Access</strong> — request a copy of personal data we hold about you.</li>
      <li><strong>Correct</strong> — ask us to fix anything inaccurate.</li>
      <li><strong>Delete</strong> — request removal of your data ("right to be forgotten").</li>
      <li><strong>Opt out</strong> — withdraw consent and unsubscribe at any time.</li>
      <li><strong>Object</strong> — object to specific processing activities.</li>
    </ul>
    <p>
      To exercise any of these rights, email{' '}
      <a href="mailto:hello@icraftz.com">hello@icraftz.com</a>. We will respond
      within 30 days.
    </p>

    <h2>5. Cookies</h2>
    <p>
      iCraftz uses only essential, first-party cookies needed to remember your
      language preference and keep you logged in to the admin area. We do not
      use third-party advertising cookies or cross-site tracking.
    </p>

    <h2>6. Data Retention</h2>
    <p>
      We retain waitlist emails until you ask us to delete them. Contact
      messages are retained for up to 24 months for response continuity.
      Backups are encrypted and rotated.
    </p>

    <h2>7. Security</h2>
    <p>
      We use industry-standard encryption in transit (TLS) and at rest. Access
      to production data is limited to authorised iCraftz personnel and is
      audited.
    </p>

    <h2>8. Children</h2>
    <p>
      iCraftz is intended for users 13 years and older. We do not knowingly
      collect data from children under 13. If you believe a minor has provided
      us with personal data, contact us and we will promptly delete it.
    </p>

    <h2>9. Changes to This Policy</h2>
    <p>
      We may update this policy as the platform evolves. The latest version
      will always be at{' '}
      <a href="/privacy">icraftz.com/privacy</a> with an updated effective date.
    </p>

    <h2>10. Contact for Privacy Queries</h2>
    <p>
      For any privacy or data-protection question, write to us at{' '}
      <a href="mailto:hello@icraftz.com">hello@icraftz.com</a>.
    </p>
  </Page>
);

export default PrivacyPage;
